import { useState, useCallback } from 'react'
import { MECETable, StrategyMap } from '@models/index'
import { MECETableEditorSimple } from '../editors/MECETableEditorSimple'
import { StrategyMapEditorSimple } from '../editors/StrategyMapEditorSimple'
import './Gate3Renderer.css'

interface Gate3RendererProps {
  data: {
    mece_table: MECETable | null
    strategy_map: StrategyMap | null
  }
  onApprove: (selectedPillars?: string[]) => void  // Phase 1.1: Accept selected pillars
  conversationId: string  // ADDED - required for editor
}

export function Gate3Renderer({ data, onApprove, conversationId }: Gate3RendererProps) {
  const [approved, setApproved] = useState(false)

  // Phase 1.1: Track selected pillars
  const [selectedPillars, setSelectedPillars] = useState<string[]>(() => {
    // Initialize with all pillar names
    return data.mece_table?.pillars?.map(p => p.name) || []
  })

  // Phase 1.1: Handler for pillar selection changes from MECETableEditorSimple
  const handlePillarSelectionChange = useCallback((pillars: string[]) => {
    setSelectedPillars(pillars)
  }, [])
  // Handle missing data gracefully
  if (!data || !data.mece_table) {
    return (
      <div className="gate3-container">
        <div className="gate-error">
          ⚠️ MECE table data not available. Please try regenerating the analysis.
        </div>
      </div>
    )
  }

  return (
    <div className="gate3-container">
      <div className="gate3-intro">
        <p>
          Review the synthesized MECE framework below. After approval, the Strategy Map
          will be generated for complete strategic analysis.
        </p>
      </div>

      {/* MECE Table - NOW WITH SIMPLE EDITOR */}
      <section className="mece-section">
        <h4>📊 MECE Scenario Analysis Table</h4>
        <MECETableEditorSimple
          data={data.mece_table}
          conversationId={conversationId}
          onSaveSuccess={() => {
            console.log('[Gate3] MECE table saved successfully')
          }}
          isSelectionModeEnabled={true}
          onPillarSelectionChange={handlePillarSelectionChange}
          initialSelectedPillars={selectedPillars}
        />

        {/* Phase 1.1: Selection summary */}
        {selectedPillars.length < (data.mece_table?.pillars?.length || 0) && (
          <div className="selection-info">
            ℹ️ {selectedPillars.length} of {data.mece_table?.pillars?.length || 0} pillars will be included in the Strategy Map
          </div>
        )}
      </section>

      {/* Strategy Map - Only show if available (after Gate 3 approval) */}
      {data.strategy_map && (
        <section className="strategy-map-section">
          <h4>🎯 Strategy Map (Balanced Scorecard)</h4>
          <StrategyMapEditorSimple
            data={data.strategy_map}
            conversationId={conversationId}
            onSaveSuccess={() => {
              console.log('[Gate3] Strategy Map saved successfully')
            }}
          />
        </section>
      )}

      {/* Actions */}
      <div className="gate-actions">
        <button
          className={`approve-btn primary ${approved ? 'approved' : ''}`}
          onClick={() => {
            setApproved(true)
            onApprove(selectedPillars)  // Phase 1.1: Pass selected pillars
          }}
          disabled={selectedPillars.length === 0}
        >
          ✓ Approve & Generate Strategy Map
          {selectedPillars.length < (data.mece_table?.pillars?.length || 0) && (
            <span className="pillar-count">({selectedPillars.length} pillars)</span>
          )}
        </button>
      </div>
    </div>
  )
}
